import { effect, inject, Injectable, signal } from "@angular/core";
import { LatLon } from "src/app/shared/components/bubble-map/bubble-map.component";
import { GatewayData, GatewaysData } from "../models/gateway.model";
import { PlaceEquipments, PlacesEquipments } from "../models/place.model";
import { GatewayStore } from "./gateway.store";
import { MeterStore } from "./meter.store";
import { PlacesStore } from "./places.store";
import { ShowerheadStore } from "./showerhead.store";
import { UserStore } from "./user.store";
import { AlertStore } from "./alert.store";

const LOG_TAG = "[SHARED-STORE]";

@Injectable({
  providedIn: "root",
})
export class SharedStore {
  private placeStore: PlacesStore = inject(PlacesStore);
  private meterStore: MeterStore = inject(MeterStore);
  private showerheadStore: ShowerheadStore = inject(ShowerheadStore);
  private userStore: UserStore = inject(UserStore);
  private gatewayStore: GatewayStore = inject(GatewayStore);
  private alertStore: AlertStore = inject(AlertStore);

  // ---------------------
  // --- place equipements
  // ---------------------

  private placesEquipements = signal<PlacesEquipments>(null);
  readonly placesEquipments$ = this.placesEquipements.asReadonly();

  // ---------------------
  // --- gateway data
  // ---------------------

  private gatewaysData = signal<GatewaysData>(null);
  readonly gatewaysData$ = this.gatewaysData.asReadonly();

  // ---------------------
  // --- LIFE CYCLE METHODS
  // ---------------------

  public constructor() {
    // place equipements
    effect(
      () => {
        const placesById = this.placeStore.placesById$();
        const metersById = this.meterStore.metersById$();
        const showerheadsById = this.showerheadStore.showerheadsById$();
        const user = this.userStore.user$();

        if (!user || !placesById || Object.keys(placesById).length === 0)
          return;

        console.log(
          LOG_TAG + "EFFECT refresh place equipements from list changes"
        );

        const placesEquipements: PlacesEquipments = {};
        Object.entries(placesById).forEach(([placeId, place]) => {
          const placeEquipements: PlaceEquipments = {
            showerheadUuids: [],
            deepShowerheadUuids: [],
            meterUuids: [],
            rootMeterUuids: [],
          };
          if (
            user.canViewMeters() &&
            metersById &&
            Object.keys(metersById).length > 0
          ) {
            const rootMeters = this.meterStore.getMetersFromPlaceId(
              parseInt(placeId)
            );
            let deepMeterUuids = [];
            rootMeters.map((meter) => {
              placeEquipements.rootMeterUuids.push(meter.device_uuid);
              // deep meter uuids
              deepMeterUuids.push(meter.device_uuid);

              if (Array.isArray(meter.deepChildrenIds)) {
                deepMeterUuids = deepMeterUuids.concat(meter.deepChildrenIds);
              }
            });
            placeEquipements.meterUuids = deepMeterUuids;
          }
          if (
            user.canViewSh() &&
            showerheadsById &&
            Object.keys(showerheadsById).length > 0
          ) {
            const placeShowerheads =
              this.showerheadStore.getShowerheadsFromPlaceId(parseInt(placeId));
            if (
              Array.isArray(placeShowerheads) &&
              placeShowerheads.length > 0
            ) {
              placeEquipements.showerheadUuids = placeShowerheads.map(
                (sh) => sh.device_uuid
              );
            }

            // prepare placeList from deepChildrenIds of place
            let deepShUuids = [];
            const deepPlaceIds: number[] = [parseInt(placeId)].concat(
              place.deepChildrenIds || []
            );
            deepPlaceIds.map((deepPlaceId) => {
              const placeShowerheads =
                this.showerheadStore.getShowerheadsFromPlaceId(deepPlaceId);
              if (!placeShowerheads) return;

              placeShowerheads.map((placeShowerhead) => {
                if (deepShUuids.indexOf(placeShowerhead.device_uuid) < 0) {
                  deepShUuids.push(placeShowerhead.device_uuid);
                }
              });
            });

            placeEquipements.deepShowerheadUuids = deepShUuids;
          }

          placesEquipements[placeId] = placeEquipements;
        });

        this.placesEquipements.set(placesEquipements);
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const meters = this.meterStore.metersByGatewayId$();
        const showerheads = this.showerheadStore.showerheadsByGatewayId$();
        const alerts = this.alertStore.alertsByGatewayId$();
        const gateways = this.gatewayStore.gateways$();

        const gatewaysData: GatewaysData = {};

        if (!Array.isArray(gateways) || gateways.length === 0) {
          return;
        }

        gateways.forEach((gateway) => {
          const gatewayData: GatewayData = {
            showerheadUuids: [],
            meterUuids: [],
            alertIds: [],
            activeAlertIds: [],
            finishedAlertIds: [],
            location: null,
          };

          let locationCandidates: Record<string, number> = {};

          const gatewayMeters = meters[gateway.gateway_uuid];
          gatewayData.meterUuids =
            (gatewayMeters &&
              gatewayMeters.map((meter) => meter.device_uuid)) ||
            [];
          if (Array.isArray(gatewayMeters) && gatewayMeters.length > 0) {
            gatewayMeters.forEach((meter) => {
              if (meter.place_id !== null) {
                const meterLocation = this.placeStore.getLocation(
                  meter.place_id
                );
                if (meterLocation !== null) {
                  const locationString =
                    meterLocation.lat + "," + meterLocation.lon;

                  if (locationCandidates[locationString] === undefined) {
                    locationCandidates[locationString] = 0;
                  }
                  locationCandidates[locationString] += 1;
                }
              }
            });
          }

          const gatewayShowerheads = showerheads[gateway.gateway_uuid];
          gatewayData.showerheadUuids =
            (gatewayShowerheads &&
              gatewayShowerheads.map((showerhead) => showerhead.device_uuid)) ||
            [];
          if (
            Array.isArray(gatewayShowerheads) &&
            gatewayShowerheads.length > 0
          ) {
            gatewayShowerheads.forEach((showerhead) => {
              if (showerhead.place_id !== null) {
                const shLocation = this.placeStore.getLocation(
                  showerhead.place_id
                );
                if (shLocation !== null) {
                  const locationString = shLocation.lat + "," + shLocation.lon;
                  if (locationCandidates[locationString] === undefined) {
                    locationCandidates[locationString] = 0;
                  }
                  locationCandidates[locationString] += 1;
                }
              }
            });
          }

          if (Object.keys(locationCandidates).length > 0) {
            // sort on values desc
            const sortedLocationCandidates = Object.entries(
              locationCandidates
            ).sort(([, v1], [, v2]) => v2 - v1);

            const gtwLocationArray = sortedLocationCandidates[0][0].split(",");

            gatewayData.location = new LatLon(
              Number(gtwLocationArray[0]),
              Number(gtwLocationArray[1])
            );
          }

          const gatewayAlerts = alerts[gateway.gateway_uuid];

          if (Array.isArray(gatewayAlerts) && gatewayAlerts.length > 0) {
            gatewayAlerts.forEach((alert) => {
              gatewayData.alertIds.push(alert.id);
              if (alert.is_active) {
                gatewayData.activeAlertIds.push(alert.id);
              } else {
                gatewayData.finishedAlertIds.push(alert.id);
              }
            });
          }

          gatewaysData[gateway.gateway_uuid] = gatewayData;
        });

        this.gatewaysData.set(gatewaysData);
      },
      { allowSignalWrites: true }
    );
  }
}
